import { TransactionReceipt } from '@ethersproject/providers'
import { ChainId } from '../../constants'
import { StoredCurrency } from '../../model'
import { StakingOption } from '../../pages/token/staking/utils'

export const TRANSACTION_TYPES_WITH_ORDER_STATUS = ['DEPOSIT', 'WITHDRAW', 'BUY', 'SELL']

export type OrderStatusString = 'enqueued' | 'executed' | 'reverted' | 'failed'

export interface OrderStatus {
  type: string
  validAfterTimestamp: string
  status: OrderStatusString
  errorMessage?: string
}

export type TransactionType = TransactionInfo['type']

export type ApproveInfo = {
  type: 'APPROVE'
  symbol: string
}

export type ApproveLiquidityInfo = {
  type: 'APPROVE_LIQUIDITY'
  symbol0: string
  symbol1: string
}

export type TransferInfo = {
  type: 'TRANSFER'
  value: string
}

export type BuyInfo = {
  type: 'BUY'
  currencyIn: StoredCurrency
  currencyOut: StoredCurrency
  expectedValueIn: string
  maxValueIn: string
  valueOut: string
  gasPrepay: string
  slippageTolerance: string
  fees: string
}

export type SellInfo = {
  type: 'SELL'
  currencyIn: StoredCurrency
  currencyOut: StoredCurrency
  valueIn: string
  expectedValueOut: string
  minValueOut: string
  gasPrepay: string
  slippageTolerance: string
  fees: string
}

export type DepositInfo = {
  type: 'DEPOSIT'
  currency0: StoredCurrency
  currency1: StoredCurrency
  value0: string
  value1: string
  expectedLiquidity: string
  gasPrepay: string
  slippageTolerance: string
  fees: string
}

export type WithdrawInfo = {
  type: 'WITHDRAW'
  currency0: StoredCurrency
  currency1: StoredCurrency
  liquidityIn: string
  expectedValue0Out: string
  expectedValue1Out: string
  gasPrepay: string
  slippageTolerance: string
  fees: string
}

export type TimeReleaseClaimInfo = {
  type: 'CLAIM'
  option1: string
  option2: string
}

export type StakingDepositInfo = {
  type: 'STAKE'
  value: string
  stakingOption: StakingOption
}

export type StakingClaimInfo = {
  type: 'CLAIM_REWARDS'
  stakingOption: StakingOption
  value: string
}

export type StakingWithdrawInfo = {
  type: 'WITHDRAW_STAKE'
  stakingOption: StakingOption
  value: string
}

export type TransactionInfo =
  | TransferInfo
  | ApproveInfo
  | ApproveLiquidityInfo
  | BuyInfo
  | SellInfo
  | DepositInfo
  | WithdrawInfo
  | TimeReleaseClaimInfo
  | StakingDepositInfo
  | StakingClaimInfo
  | StakingWithdrawInfo

interface SellAmounts {
  type: 'SELL'
  amount0Out: string
  amount1Out: string
}

interface BuyAmounts {
  type: 'BUY'
  amount0In: string
  amount1In: string
}

interface DepositAmounts {
  type: 'DEPOSIT'
  amount0: string
  amount1: string
  liquidityOut: string
}

interface WithdrawAmounts {
  type: 'WITHDRAW'
  amount0Out: string
  amount1Out: string
}

export type Amounts = SellAmounts | BuyAmounts | DepositAmounts | WithdrawAmounts

export interface TransactionToSave {
  chainId: ChainId
  from: string
  hash: string
  nonce: number
  txInfo: TransactionInfo
}

export interface StoredTransaction {
  from: string
  hash: string
  nonce: number
  submittedAt: number
  lastCheckedBlockNumber?: number
  receipt?: TransactionReceipt
  txInfo: TransactionInfo
  orderStatus?: OrderStatus
  droppedAndReplaced?: boolean
  amounts?: Amounts
  executionHash?: string
  ethRefund?: string
}

export type StoredTransactions = {
  [T in ChainId]?: StoredTransaction[]
}

export const DEFAULT_STORED_TRANSACTIONS: StoredTransactions = {}

export const isPending = (tx: StoredTransaction) => {
  return !tx.droppedAndReplaced && (!tx.receipt || isEnqueuedOrder(tx))
}

export const isOrder = (tx: StoredTransaction) => {
  return TRANSACTION_TYPES_WITH_ORDER_STATUS.includes(tx.txInfo.type)
}

export const isEnqueuedOrder = (tx: StoredTransaction) => {
  return isOrder(tx) && (!tx.orderStatus || !['executed', 'reverted', 'failed'].includes(tx.orderStatus.status))
}
